


import { AllQestSingleScore } from 'utils/game/gameService';
import { getLeaderboardData } from 'utils/gameApplication/gamePlayService';
import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { ScoreContext } from '../GamePreview';
import { motion } from 'framer-motion';
import { ProfileContext } from '../EntirePreview';
import { useParams } from 'react-router-dom';
import { Buttons } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import svgPaths from '../../../../imports/svg-knollny4nu';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../../../../assets/img/NewUI_Images/top-nav-bg.png';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  CustomCoinIcon,
  CustomHomeIcon,
  CustomPauseIcon,
  CustomPlayIcon,
  CustomMapIcon,
  CustomExitIcon,
  CustomMedalIcon,
  CustomRankingIcon,
  CustomSettingsIcon,
} from '../../../../components/ui/CustomIcons';
import imgBackground from '/images/background.png';
import { useColor } from '../../../../components/ui/ColorContext';
import ColorPicker from '../../../../components/ui/ColorPicker';
import { Slider } from '../../../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../../../components/ui/dialog';
type QuestScoreType = {
  totalQuestScore: number;
};
// Helper function to convert hex to RGB
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 255, 187'; // fallback to default green
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};

// Moved TopNavButton component outside to prevent recreation
const TopNavButton: React.FC<{ icon: string; onClick?: () => void }> =
  React.memo(({ icon, onClick }) => {
    const { primaryColor } = useColor();
    const primaryColorRgb = hexToRgb(primaryColor);

    const getButtonName = () => {
      switch (icon) {
        case 'home':
          return 'Home';
        case 'exit':
          return 'Exit';
        case 'ranking':
          return 'Ranking';
        case 'settings':
          return 'Settings';
        case 'coin':
          return 'Coin';
        case 'pause':
          return 'Pause';
        case 'play':
          return 'Play';
        default:
          return '';
      }
    };

    const IconElement = () => {
      switch (icon) {
        case 'home':
          return <CustomHomeIcon color={primaryColor} size={20} />;
        case 'exit':
          return <CustomExitIcon color={primaryColor} size={20} />;
        case 'ranking':
          return <CustomRankingIcon color={primaryColor} size={20} />;
        case 'settings':
          return <CustomSettingsIcon color={primaryColor} size={20} />;
        case 'pause':
          return <CustomPauseIcon color={primaryColor} size={20} />;
        case 'play':
          return <CustomPlayIcon color={primaryColor} size={20} />;
        default:
          return <IconComponent type={icon} color={primaryColor} />;
      }
    };

    return (
      <div className="group relative">
        <Buttons
          variant="ghost"
          size="sm"
          onClick={onClick}
          className="group relative h-12 w-12 rounded-2xl !px-0 py-0 transition-transform duration-300 hover:scale-110"
          style={{
            borderRadius: '16px',
            boxShadow: '0px 0px 23.386px 0px rgba(0, 0, 0, 0.50)',
            background: `linear-gradient(151.477deg, rgba(${primaryColorRgb}, 0.3), rgb(0, 0, 0))`,
          }}
        >
          <div
            className="absolute bottom-[1px] left-[1px] right-[1px] top-[1px] flex items-center justify-center rounded-2xl"
            style={{
              background: `linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${primaryColorRgb}, 0.5) 188.68%)`,
              borderRadius: '16px',
              zIndex: 1,
            }}
          >
            {icon === 'ranking' ? (
              <CustomRankingIcon color={primaryColor} className="!h-10 !w-10" />
            ) : icon === 'settings' ? (
              <CustomSettingsIcon
                color={primaryColor}
                className="-mb-1 !h-10 !w-10"
              />
            ) : (
              <IconElement />
            )}
          </div>
        </Buttons>
        <div className="-bottom-15 pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 transform opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <div
            className="min-w-22 whitespace-nowrap rounded-xl px-6 py-3 text-lg font-semibold text-white"
            style={{
              background: `linear-gradient(275.041deg, rgba(${primaryColorRgb}, 0.7) 7.25%, rgba(0, 0, 0, 0.8) 84.803%)`,
              border: `2px solid ${primaryColor}`,
              boxShadow: `0px 0px 15.9542px 0px rgba(${primaryColorRgb}, 0.8)`,
            }}
          >
            {getButtonName()}
          </div>
        </div>
      </div>
    );
  });

// Moved StatusBar component outside to prevent recreation
const StatusBar: React.FC<{ label: string; icon?: string; newlabel: string }> =
  React.memo(({ label, icon, newlabel }) => {
    const { primaryColor } = useColor();
    const primaryColorRgb = hexToRgb(primaryColor);

    return (
      <div className="flex items-center">
        {icon && (
          <div className="group relative">
            <Buttons
              variant="ghost"
              size="sm"
              className="relative h-12 w-12 rounded-2xl !px-0 py-0"
              style={{
                borderRadius: '16px',
                boxShadow: '0px 0px 23.386px 0px rgba(0, 0, 0, 0.50)',
                background: `linear-gradient(151.477deg, rgba(${primaryColorRgb}, 0.3), rgb(0, 0, 0))`,
              }}
            >
              <div
                className="absolute bottom-[1px] left-[1px] right-[1px] top-[1px] flex items-center justify-center rounded-2xl backdrop-blur-sm"
                style={{
                  background: `linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${primaryColorRgb}, 0.5) 188.68%)`,
                  borderRadius: '16px',
                  zIndex: 1,
                }}
              >
                {icon === 'coin' ? (
                  <CustomCoinIcon
                    color={primaryColor}
                    className="!h-12 !w-12"
                  />
                ) : icon === 'progress' ? (
                  <span
                    className="font-rubik text-shadow-[0px_2.867px_8.6px_rgba(0,_255,_187,_0.30)] text-[0.813rem] font-medium tracking-[-0.007rem] text-white"
                    style={{ color: primaryColor }}
                  >
                    {label}%
                  </span>
                ) : (
                  <IconComponent type={icon} color={primaryColor} />
                )}
              </div>
            </Buttons>
            <div className="-bottom-15 pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 transform opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div
                className="min-w-22 whitespace-nowrap rounded-xl px-6 py-3 text-lg font-semibold text-white"
                style={{
                  background: `linear-gradient(275.041deg, rgba(${primaryColorRgb}, 0.7) 7.25%, rgba(0, 0, 0, 0.8) 84.803%)`,
                  border: `2px solid ${primaryColor}`,
                  boxShadow: `0px 0px 15.9542px 0px rgba(${primaryColorRgb}, 0.8)`,
                }}
              >
                {icon === 'coin' ? 'Scores' : 'Progress'}
              </div>
            </div>
          </div>
        )}
        {icon === 'progress' ? (
          <div className="relative -ml-2.5 min-w-24 flex-1">
            <div
              className="h-8 w-full overflow-hidden rounded-[0.625rem]"
              style={{
                background: `linear-gradient(179.484deg, rgb(0, 0, 0) 17.606%, rgba(${primaryColorRgb}, 0.4) 188.68%)`,
                border: `1px solid rgba(${primaryColorRgb}, 0.1)`,
                boxShadow: '0px 0px 15.3543px 0px rgba(0,0,0,0.5)',
              }}
            >
              <div
                className="h-full rounded-[0.625rem] shadow-[0px_0px_2.378px_0px_rgba(0,_255,_187,_0.30)_inset,_0px_2.867px_8.6px_0px_rgba(0,_255,_187,_0.60)] transition-all duration-500 ease-out"
                style={{
                  width: `${Number(label)}%`,
                  backgroundColor: primaryColor,
                  boxShadow: `0px 0px 9.93511px 0px rgba(184,184,184,0.2)`,
                }}
              />
            </div>
          </div>
        ) : (
          <Badge
            variant="secondary"
            className="font-rubik -ml-1.5 flex h-8 min-w-24 items-center rounded-[0.625rem] px-4"
            style={{
              background: `linear-gradient(170.484deg, rgb(0, 0, 0) 17.606%, rgba(${primaryColorRgb}, 0.4) 188.68%)`,
              border: `1px solid rgba(${primaryColorRgb}, 0.2)`,
              boxShadow: '0px 0px 23.3864px 0px rgba(0,0,0,0.5)',
            }}
          >
            <span className="text-[0.813rem] font-medium text-white">
              {/* {label}   / */}
              {newlabel}
            </span>
          </Badge>
        )}
      </div>
    );
  });

const IconComponent: React.FC<{ type: string; color: string }> = ({
  type,
  color,
}) => {
  const getIconPath = () => {
    switch (type) {
      case 'ranking':
        return svgPaths.p1a822c00;
      case 'home':
        return svgPaths.p3bf15400;
      case 'map':
        return svgPaths.p285e4100;
      case 'settings':
        return svgPaths.p2037cc80;
      case 'coin':
        return svgPaths.p1444db00;
      default:
        return svgPaths.p1444db00;
    }
  };

  return (
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
      <path d={getIconPath()} fill={color} />
    </svg>
  );
};

let names: any = [];
interface TopMenuProps {
  isLeaderboardEnabled: boolean;
  setIsLeaderboardEnabled: (opt: boolean) => void;
  dontShowTopMenu: boolean;
  preloadedAssets: any;
  currentScreenId: number;
  setCurrentScreenId: (id: number) => void;
  isSettingOpen: boolean;
  setIsSettingOpen: (opt: boolean) => void;
  setHomeLeaderBoard: (id: number) => void;
  profileData: any;
  gameInfo: any;
  demoBlocks: any;
  data: any;
  setAudioObj: (obj: any) => void;
  audioObj: any;
  questState: any;
  EnumType: any;
  learnerPlayList: any;
  setLearnerPlayingDetails: any;
  handleMusicVolume: any;
  formData?: any;
  imageSrc?: any;
  getData?: any;

  homeLeaderBoard?: any;

  setPlayerTodayScore: any;
  playerTodayScore: any;
  setCurrentQuestNo: any;
  setFirstLoading: any;
  questWisePlayerScore: any;
  setQuestWisePlayerScore: any;
  toggleAudioPlayPause?: () => void;
  isAudioPaused?: boolean;
  playMenuSound: () => void;
  type: any;
}

const TopMenuBarFutureTheme: React.FC<TopMenuProps> = React.memo(
  ({
    setIsLeaderboardEnabled,
    isLeaderboardEnabled,
    dontShowTopMenu,
    preloadedAssets,
    currentScreenId,
    setCurrentScreenId,
    isSettingOpen,
    setIsSettingOpen,
    setHomeLeaderBoard,
    profileData,
    gameInfo,
    demoBlocks,
    data,
    setAudioObj,
    audioObj,
    questState,
    EnumType,
    learnerPlayList,
    setLearnerPlayingDetails,
    handleMusicVolume,
    imageSrc,
    formData,
    getData,
    playMenuSound,
    homeLeaderBoard,

    setPlayerTodayScore,
    playerTodayScore,
    setCurrentQuestNo,
    setFirstLoading,
    setQuestWisePlayerScore,
    questWisePlayerScore,
    toggleAudioPlayPause,
    isAudioPaused,
    type,
  }) => {
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<'department' | 'overall'>(
      'overall',
    );
    const useData = useContext(ProfileContext);
    const { learner_game_play_id } = useParams();
    const [progressPercent, setProgressPercent] = useState<any>(0);
    const [shuffledUsers, setShuffledUsers] = useState<any[]>([]);
    const [allTimeClicked, setAllTimeClicked] = useState(false);
    const [newSortedUserss, setNewSortedUserss] = useState<any[]>([]);
    const [sortedUsers, setSortedUsers] = useState<any[]>([]);
    const playerInfo = useContext(ProfileContext);
    const contextValue = useContext(ScoreContext);
    const { profile, setProfile } =
      contextValue !== null
        ? contextValue
        : { profile: null, setProfile: null };
    const [sortAse, setSortAse] = useState({ daily: true, allTime: true });
    const [departmentLeaderboardData, setDepartmentLeaderboardData] = useState<
      any[]
    >([]);

    const [companyLeaderboardData, setCompanyLeaderboardData] = useState<any[]>(
      [],
    );

    const [activeLeaderboard, setActiveLeaderboard] = useState<
      'department' | 'company'
    >('company'); // Track active leaderboard

    const [isIOS, setIsIOS] = useState(false);
    const [isLeaderboardClicked, setIsLeaderboardClicked] = useState(false);

    const { primaryColor } = useColor();
    const primaryColorRgb = useMemo(
      () => hexToRgb(primaryColor),
      [primaryColor],
    );

    useEffect(() => {
      const userAgent =
        window.navigator.userAgent || (window.navigator as any).vendor || '';

      // ✅ Detect iPhone or iPad (but not Mac or Android)
      if (/iPhone|iPad/i.test(userAgent) && !/Macintosh/i.test(userAgent)) {
        setIsIOS(true);
      }
    }, []);

    // useEffect(() => {
    //   const getAllLearnerScores = async () => {
    //     const getLeaderboardDatas = await getLeaderboardData(learner_game_play_id);
    //     if (getLeaderboardDatas?.departmentLeaderboardData) {
    //       setDepartmentLeaderboardData(getLeaderboardDatas?.departmentLeaderboardData);
    //     }
    //     if (getLeaderboardDatas?.companyLeaderboardData) {
    //       setCompanyLeaderboardData(getLeaderboardDatas?.companyLeaderboardData);
    //     }
    //   };
    //   getAllLearnerScores();
    // }, [learner_game_play_id]);

    useEffect(() => {
      if (!profile) return;

      const currentDate = new Date();
      const formattedDate = `${String(currentDate.getDate()).padStart(
        2,
        '0',
      )}-${String(currentDate.getMonth() + 1).padStart(
        2,
        '0',
      )}-${currentDate.getFullYear()}`;

      // recompute daily scores
      const sums: Record<number, number> = {};
      profile?.score?.forEach((s: any) => {
        if (s.scoreEarnedDate === formattedDate) {
          sums[s.quest] = (sums[s.quest] || 0) + s.score;
        }
      });

      const replaySums: Record<number, number> = {};
      profile?.replayScore?.forEach((s: any) => {
        if (s.scoreEarnedDate === formattedDate) {
          replaySums[s.quest] = (replaySums[s.quest] || 0) + s.score;
        }
      });

      const todayTotal = Object.entries(sums).reduce((tot, [quest, score]) => {
        const q = Number(quest);
        if (replaySums[q]) {
          return tot + Math.max(replaySums[q], score);
        }
        return tot + score;
      }, 0);

      setPlayerTodayScore(todayTotal);
    }, [profile?.score, profile?.replayScore, names]);

    useEffect(() => {
      const currentDate = new Date();
      // Get day, month, and year
      const day = String(currentDate.getDate()).padStart(2, '0');
      const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
      const year = currentDate.getFullYear();
      // Format date as DD-MM-YYYY
      const formattedDate = `${day}-${month}-${year}`;

      const scores = profile?.score;
      const sums: any = {};
      scores?.forEach((score: any) => {
        const quest = score.quest;
        if (!sums[quest]) {
          sums[quest] = 0;
        }
        if (score.scoreEarnedDate === formattedDate) {
          sums[quest] += score.score;
        }
      });
      let getFinalscores = {};
      Object.entries(sums).forEach(([quest, score]) => {
        const IntQuest = parseInt(quest);
        const newQuest = { ...getFinalscores, [IntQuest]: score };
        getFinalscores = { ...newQuest };
      });
      // const TodayTotalScore = Object.entries(getFinalscores).reduce((tot: number, acc: any) => {
      //     tot += acc[1];
      //   return tot;
      // }, 0);
      // setPlayerTodayScore(TodayTotalScore);
      const Replayscores =
        profile?.replayScore?.length > 0 ? profile?.replayScore : null;
      
      const Replaysums: { [key: number]: number } = {};
      Replayscores?.forEach((score: any) => {
        const quest = score?.quest;
        if (!Replaysums[quest]) {
          Replaysums[quest] = 0;
        }
        if (score.scoreEarnedDate === formattedDate) {
          Replaysums[quest] += score.score;
        }
      });

      let getReplayFinalscores: { [key: number]: number } = {};
      Object.entries(Replaysums).forEach(([quest, score]) => {
        const IntQuest = parseInt(quest);
        getReplayFinalscores = { ...getReplayFinalscores, [IntQuest]: score };
      });

      const TodayTotalScore = Object.entries(getFinalscores).reduce(
        (tot: number, acc: any) => {
          let questNo = acc[0];
          let questHasReplay = Object.keys(getReplayFinalscores).some(
            (quest) => quest === questNo,
          );
          if (questHasReplay) {
            getReplayFinalscores[questNo] > acc[1]
              ? (tot += getReplayFinalscores[questNo])
              : (tot += acc[1]);
          } else {
            tot += acc[1];
          }
          return tot;
        },
        0,
      );
      setPlayerTodayScore(TodayTotalScore);
      const mergedUsersPlayers = names.sort((a: any, b: any) => {
        // Sort by allTimeScore in descending order

        if (b.allTimeScore !== a.allTimeScore) {
          return b.allTimeScore - a.allTimeScore;
        }
        // If allTimeScores are equal, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
      //Sorted Using Score-starts for score position
      let sortedUsingScore = [...mergedUsersPlayers].sort((a, b) => {
        // Sort by daily score in descending order
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // If scores are equal, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
      let dailyPositionIndex = -1;
      sortedUsingScore = sortedUsingScore.map(
        (usrScore: any, index: number) => {
          dailyPositionIndex = index + 1;
          let alltimePositionIndex = -1;
          let alltimePosition = mergedUsersPlayers.find(
            (userDetail: any, indexvalue: number) => {
              if (
                userDetail.name == usrScore.name &&
                userDetail.score == usrScore.score &&
                userDetail.allTimeScore == usrScore.allTimeScore
              ) {
                alltimePositionIndex = indexvalue + 1;
                return true;
              }
            },
          );
          return {
            ...usrScore,
            dailyPosition: dailyPositionIndex,
            alltimePosition: alltimePositionIndex,
          };
        },
      );
      let playerIndex: any;
      playerIndex = sortedUsingScore.findIndex(
        (x) =>
          x.name ===
          (learnerPlayList?.LearnerProfile?.nickName ??
            learnerPlayList?.LearnerProfile?.name),
      );
      // playerIndex = sortedUsingScore.findIndex(x => x.name === 'Player');

      if (playerIndex !== -1) {
        // Remove it from its current position

        const unShiftedPlayer = sortedUsingScore.splice(playerIndex, 1)[0];

        // Add it to the beginning of the array
        sortedUsingScore.unshift(unShiftedPlayer);

        setShuffledUsers(sortedUsingScore);
      }
    }, [playerTodayScore, names]);

    const handleHome = () => {
      if (homeLeaderBoard) {
        setCurrentScreenId(homeLeaderBoard);
        setHomeLeaderBoard(null);
      } else {
        if (gameInfo) {
          if (
            gameInfo?.gameData?.gameIsShowReflectionScreen === 'true' &&
            gameInfo?.reflectionQuestions.length > 0
          ) {
            setCurrentScreenId(3); // Navigate to Reflection screen
          } else if (gameInfo?.gameData?.gameIsShowTakeaway === 'true') {
            setCurrentScreenId(7); // Navigate to Takeaway screen
          } else {
            setCurrentScreenId(5); // Navigate to Thank you screen
          }
        }
      }
    };

    const handleNext = () => {
      setFirstLoading(true);
      useData?.setMotionEffect(true);
      setTimeout(() => {
        getData(data);
      }, 300);
    };

    const handleAllTimeClick = async (type: string) => {
      let newSortedUsers = [...shuffledUsers];
      if (type === 'daily') {
        setSortAse((prev: any) => ({ ...prev, daily: !prev.daily }));

        newSortedUsers = [...shuffledUsers].sort((a: any, b: any) => {
          if (sortAse.daily) {
            if (a.score !== b.score) {
              return a.score - b.score;
            } else {
              return b.name.localeCompare(a.name);
            }
          } else {
            // Sort by score in descending order
            if (b.score !== a.score) {
              return b.score - a.score;
            } else {
              return a.name.localeCompare(b.name);
            }
          }
        });
        newSortedUsers = newSortedUsers.map((row: any, index: number) => ({
          ...row,
        }));
      } else if (type === 'alltime') {
        setSortAse((prev: any) => ({ ...prev, allTime: !prev.allTime }));
        // Sort the users by all-time score
        newSortedUsers = [...shuffledUsers].sort((a: any, b: any) => {
          if (sortAse.allTime) {
            if (b.allTimeScore !== a.allTimeScore) {
              return b.allTimeScore - a.allTimeScore;
            } else {
              return a.name.localeCompare(b.name);
            }
          } else {
            // Sort by allTimeScore in descending order
            if (a.allTimeScore !== b.allTimeScore) {
              return a.allTimeScore - b.allTimeScore;
            } else {
              return b.name.localeCompare(a.name);
            }
          }
        });
        newSortedUsers = newSortedUsers.map((row: any, index: number) => ({
          ...row,
        }));
      }

      // If playerData exists, move it to the first position
      const playerData = newSortedUsers.find(
        (user) => user.name === playerInfo?.profileData?.name,
      );
      if (playerData) {
        const playerIndex = newSortedUsers.findIndex(
          (x) => x.name === playerInfo?.profileData?.name,
        );

        if (playerIndex !== -1) {
          // Remove it from its current position
          const unShiftedPlayer = newSortedUsers.splice(playerIndex, 1)[0];
          // Add it to the beginning of the array
          newSortedUsers.unshift(unShiftedPlayer);
        }
      }
      // Update the state with the new sorted users
      setShuffledUsers(newSortedUsers);
    };

    const containerRef = useRef<any>(null);
    let lastScrollTop = 0;

    useEffect(() => {
      const container = containerRef?.current;
      if (!container) return; // Early return if container is not available
      const handleScroll = () => {
        let currentScrollTop = container?.scrollTop;

        if (currentScrollTop > lastScrollTop) {
          container.classList.add('scrollbar-down');
        } else {
          // Scrolling up
          container.classList.remove('scrollbar-down');
          // container.classList.remove('content-box');
        }

        lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop; // For Mobile or negative scrolling
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }, []);

    const previousNavigation = async () => {
      const LastquestNo = parseInt(profile.currentQuest);
      setCurrentQuestNo(LastquestNo);
      setCurrentScreenId(6);
    };
    const isDepartmentEmpty = departmentLeaderboardData.length === 0;

    const handleDepartmentClick = () => {
      if (!isDepartmentEmpty) {
        setActiveLeaderboard('department');
      }
    };

    // const handleDepartmentClick = () => {
    //   setActiveLeaderboard('department');
    // };

    // Handle click on "Overall" button
    const handleOverallClick = () => {
      setActiveLeaderboard('company');
    };

    const [geFinalscorequest, SetFinalscore] = useState(null);

    // const [progressPercent, setProgressPercent] = useState<any>(0);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const [showIsLeaderboardEnabled, showSetIsLeaderboardEnabled] =
      useState<boolean>(null);

    const [progressShow, setProgressShow] = useState(null);

    useEffect(() => {
      const fetchLeaderboard = async () => {
        const getLeaderboardDatas = await getLeaderboardData(
          learner_game_play_id,
        );

        if (getLeaderboardDatas?.departmentLeaderboardData) {
          setDepartmentLeaderboardData(
            getLeaderboardDatas.departmentLeaderboardData,
          );
        }

        if (getLeaderboardDatas?.companyLeaderboardData) {
          setCompanyLeaderboardData(getLeaderboardDatas.companyLeaderboardData);
        }
      };

      // ## IMPORTANT CONDITION 🔥
      if (profile && isLeaderboardClicked) {
        fetchLeaderboard();
      }
    }, [learner_game_play_id, profile, isLeaderboardClicked]); // ## added dependency

    const NavigationArrow: React.FC<{
      direction: 'left' | 'right';
      onClick: () => void;
    }> = ({ direction, onClick }) => {
      return (
        <Buttons
          variant="ghost"
          size="sm"
          onClick={onClick}
          className="h-10 w-[3.75rem] rounded-3xl px-4 py-2 transition-all duration-200 hover:scale-110"
          style={{
            background: `linear-gradient(${
              direction === 'left' ? '275.041deg' : '90deg'
            },
                  rgba(${primaryColorRgb}, 0.6) 7.25%, rgba(${primaryColorRgb}, 0.15) 84.803%)`,
            border: `1px solid ${primaryColor}`,
            boxShadow: `0px 0px 15.9542px 0px rgba(${primaryColorRgb}, 0.3)`,
          }}
        >
          {direction === 'left' ? (
            <ChevronLeft className="!h-4 !w-4 text-white" />
          ) : (
            <ChevronRight className="!h-4 !w-4 text-white" />
          )}
        </Buttons>
      );
    };

    useEffect(() => {
      const currentDate = new Date();
      // Get day, month, and year
      const day = String(currentDate.getDate()).padStart(2, '0');
      const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
      const year = currentDate.getFullYear();
      // Format date as DD-MM-YYYY
      const formattedDate = `${day}-${month}-${year}`;

      const scores = profile?.score;
      const sums: any = {};
      scores?.forEach((score: any) => {
        const quest = score.quest;
        if (!sums[quest]) {
          sums[quest] = 0;
        }
        if (score.scoreEarnedDate === formattedDate) {
          sums[quest] += score.score;
        }
      });
      let getFinalscores = {};
      Object.entries(sums).forEach(([quest, score]) => {
        const IntQuest = parseInt(quest);
        const newQuest = { ...getFinalscores, [IntQuest]: score };
        getFinalscores = { ...newQuest };
      });

      const Replayscores =
        profile?.replayScore?.length > 0 ? profile?.replayScore : null;
      const Replaysums: { [key: number]: number } = {};
      Replayscores?.forEach((score: any) => {
        const quest = score.quest;
        if (!Replaysums[quest]) {
          Replaysums[quest] = 0;
        }
        if (score.scoreEarnedDate === formattedDate) {
          Replaysums[quest] += score.score;
        }
      });

      let getReplayFinalscores: { [key: number]: number } = {};
      Object.entries(Replaysums).forEach(([quest, score]) => {
        const IntQuest = parseInt(quest);
        getReplayFinalscores = { ...getReplayFinalscores, [IntQuest]: score };
      });

      const TodayTotalScore = Object.entries(getFinalscores).reduce(
        (tot: number, acc: any) => {
          let questNo = acc[0];
          let questHasReplay = Object.keys(getReplayFinalscores).some(
            (quest) => quest === questNo,
          );
          if (questHasReplay) {
            getReplayFinalscores[questNo] > acc[1]
              ? (tot += getReplayFinalscores[questNo])
              : (tot += acc[1]);
          } else {
            tot += acc[1];
          }
          return tot;
        },
        0,
      );
      setPlayerTodayScore(TodayTotalScore);
      const mergedUsersPlayers = names.sort((a: any, b: any) => {
        // Sort by allTimeScore in descending order

        if (b.allTimeScore !== a.allTimeScore) {
          return b.allTimeScore - a.allTimeScore;
        }
        // If allTimeScores are equal, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
      //Sorted Using Score-starts for score position
      let sortedUsingScore = [...mergedUsersPlayers].sort((a, b) => {
        // Sort by daily score in descending order
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // If scores are equal, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
      let dailyPositionIndex = -1;
      sortedUsingScore = sortedUsingScore.map(
        (usrScore: any, index: number) => {
          dailyPositionIndex = index + 1;
          let alltimePositionIndex = -1;
          let alltimePosition = mergedUsersPlayers.find(
            (userDetail: any, indexvalue: number) => {
              if (
                userDetail.name == usrScore.name &&
                userDetail.score == usrScore.score &&
                userDetail.allTimeScore == usrScore.allTimeScore
              ) {
                alltimePositionIndex = indexvalue + 1;
                return true;
              }
            },
          );
          return {
            ...usrScore,
            dailyPosition: dailyPositionIndex,
            alltimePosition: alltimePositionIndex,
          };
        },
      );
      let playerIndex: any;
      playerIndex = sortedUsingScore.findIndex(
        (x) =>
          x.name ===
          (learnerPlayList?.LearnerProfile?.nickName ??
            learnerPlayList?.LearnerProfile?.name),
      );
      // playerIndex = sortedUsingScore.findIndex(x => x.name === 'Player');

      if (playerIndex !== -1) {
        // Remove it from its current position

        const unShiftedPlayer = sortedUsingScore.splice(playerIndex, 1)[0];

        // Add it to the beginning of the array
        sortedUsingScore.unshift(unShiftedPlayer);

        setShuffledUsers(sortedUsingScore);
      }
    }, [playerTodayScore, names]);

    useEffect(() => {
      const container = containerRef?.current;
      if (!container) return; // Early return if container is not available
      const handleScroll = () => {
        let currentScrollTop = container?.scrollTop;

        if (currentScrollTop > lastScrollTop) {
          container.classList.add('scrollbar-down');
        } else {
          // Scrolling up
          container.classList.remove('scrollbar-down');
          // container.classList.remove('content-box');
        }

        lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop; // For Mobile or negative scrolling
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }, []);

    useEffect(() => {
      const scores = profile?.score;
      if (scores && scores.length > 0) {
        const sums = scores?.reduce(
          (accumulator: { [key: string]: number }, score: any) => {
            const quest = score.quest;
            if (accumulator?.[quest] != undefined) {
              accumulator[quest] = (accumulator[quest] || 0) + score.score;
              return accumulator;
            }
          },
          0,
        );
        SetFinalscore(sums);
      }
    }, []);

    const handleOverView = () => {
      setHomeLeaderBoard(currentScreenId);
      setCurrentScreenId(4);
    };
    useEffect(() => {
      let progressBarRatioforquest: any;
      const progressResult = () => {
        //calculate Progress based on screen, Need to show different progress for current screen is in story, progress of the current quest, unless  show the entire game progress
        if (currentScreenId === 2) {
        
          const currentQuestBlocks = demoBlocks[profile?.currentQuest];
          const totalblockCount =
            currentQuestBlocks && Object.keys(currentQuestBlocks).length;
          const keyWithValueOfCurrentBlock =
            currentQuestBlocks &&
            Object.keys(currentQuestBlocks).find((key: any) => {
              const obj = currentQuestBlocks[key];
              const blockPrimarySequence = obj?.blockPrimarySequence;
              if (blockPrimarySequence) {
                const hasMatchingSequence =
                  blockPrimarySequence.trim() ===
                  (data?.blockPrimarySequence || '').trim();
                 
                return hasMatchingSequence;
              }
              return false;
            });
          const progressBarRatio: any =
            keyWithValueOfCurrentBlock &&
            (parseInt(keyWithValueOfCurrentBlock) > 0
              ? (parseInt(keyWithValueOfCurrentBlock) - 1) / totalblockCount
              : 0);
          setProgressPercent(
            progressBarRatio && progressBarRatio > 0 ? progressBarRatio : 0,
          );
          const currentQuest = parseInt(profile?.currentQuest);
         
          setLearnerPlayingDetails((prev: any) => {
            const questProgress = prev.questwiseprogress ?? [];

            const existingQuest = questProgress.find(
              (item: any) => item.quest === currentQuest
            );
         
            // Don't update if existing progress is higher/equal
            // if (
            //   existingQuest &&
            //   existingQuest.progress >= (progressBarRatio && progressBarRatio > 0 ? progressBarRatio : 0)
            // ) {
            //   return prev;
            // }

            if (
  existingQuest &&
  (
    existingQuest.progress === 1 ||
    existingQuest.progress >= (
      progressBarRatio && progressBarRatio > 0
        ? progressBarRatio
        : 0
    )
  )
) {
  return prev;
}

            // Update existing quest
            if (existingQuest) {
              return {
                ...prev,
                questwiseprogress: questProgress.map((item: any) =>
                  item.quest === currentQuest
                    ? {
                        ...item,
                        progress: progressBarRatio,
                        percentage : Math.round(progressBarRatio * 100)
                      }
                    : item
                ),
              };
            }

            // Add new quest
            return {
              ...prev,
              questwiseprogress: [
                ...questProgress,
                {
                  quest: currentQuest,
                  progress: progressBarRatio,
                },
              ],
            };
          });
          if (questState[parseInt(profile?.currentQuest)] === 'Started') {
            setLearnerPlayingDetails((prev: any) => ({
              ...prev,
              
              progress: progressBarRatio,
            }));
          }
          setProgressShow(progressBarRatioforquest);
        } else {
          if (currentScreenId === 6) {
          const currentQuest = parseInt(profile?.currentQuest);
          setLearnerPlayingDetails((prev: any) => ({
            ...prev,
            questwiseprogress: (prev.questwiseprogress ?? []).map(
              (item: any) =>
                item.quest === currentQuest
                  ? {
                      ...item,
                      progress: 1,
                      percentage: 100,
                    }
                  : item
            ),
          }));
        }
          const uniqueQuestIds = [...new Set(profile?.completedLevels)]; //returns ['1', '2', '3'] if it has ['1','2','2','3']

          //collect the actually completed quest list to show the the progress
          const completedQuestList = uniqueQuestIds.filter((quest: any) => {
            const isCurrentQuestCompleted = Object.entries(questState).some(
              ([key, value]: [any, any]) => {
                return (
                  key === quest &&
                  ['replayallowed', 'completed'].includes(value)
                );
              },
            );

            return isCurrentQuestCompleted;
          });

          // const completedQuest = completedQuestList.length;
          // let gameProgress = 0;
          // if (completedQuest) {
          //   gameProgress = completedQuest / gameInfo?.gameQuest?.length;

          //   if (currentScreenId === 6) {
          //     setLearnerPlayingDetails((prev: any) => ({
          //       ...prev,

          //       progress: gameProgress,
          //     }));
          //   }
          // }
          const questWiseProgress = learnerPlayList?.questwiseprogress;

let gameProgress = 0;

if (questWiseProgress?.length) {
  const totalQuestProgress = questWiseProgress.reduce(
    (total: number, quest: any) => total + (quest.progress || 0),
    0
  );

  const totalQuests = gameInfo?.gameQuest?.length || 0;

  gameProgress = totalQuests
    ? totalQuestProgress / totalQuests
    : 0;
} else {
  // Existing flow if questwiseprogress is not available
  const completedQuest = completedQuestList.length;

  const totalQuests = gameInfo?.gameQuest?.length || 0;

  gameProgress = totalQuests
    ? completedQuest / totalQuests
    : 0;
}

if (currentScreenId === 6) {
  setLearnerPlayingDetails((prev: any) => ({
    ...prev,
    progress: gameProgress,
  }));
}

          setProgressPercent(
            gameProgress && gameProgress > 0 ? gameProgress : 0,
          );
        }
      };

      progressResult();
    }, [data,type, currentScreenId, questState]);
    useEffect(() => {
      if (gameInfo?.gameData?.gameIsShowLeaderboard === 'true') {
        showSetIsLeaderboardEnabled(true);
      } else {
        showSetIsLeaderboardEnabled(false);
      }
    }, [gameInfo?.gameData?.gameIsShowLeaderboard]);

    const handleMusicVolume1 = (sliderValue: number, type: string) => {
      if (!isNaN(sliderValue) && isFinite(sliderValue)) {
        const newVolume = sliderValue / 100;
        setLearnerPlayingDetails((prev: any) => ({
          ...prev,
          // audioVolumeValue: { bgmVolume: prev.audioVolumeValue.bgmVolume, voiceVolume: newVoiceVolume }
          audioVolumeValue: {
            bgmVolume:
              type === EnumType.BGM
                ? newVolume.toString()
                : learnerPlayList.audioVolumeValue.bgmVolume,
            voiceVolume:
              type === EnumType.VOICE
                ? newVolume.toString()
                : learnerPlayList.audioVolumeValue.voiceVolume,
          },
        }));
        setAudioObj((prev: any) => ({
          ...prev,
          type: type === EnumType.BGM ? EnumType.BGM : EnumType.VOICE, // Update type based on parameter
          Voicevolume:
            type === EnumType.VOICE
              ? newVolume.toString()
              : learnerPlayList?.audioVolumeValue?.voiceVolume
              ? learnerPlayList?.audioVolumeValue?.voiceVolume
              : 0.5,
          BGMvolume:
            type === EnumType.BGM
              ? newVolume.toString()
              : learnerPlayList?.audioVolumeValue?.bgmVolume
              ? learnerPlayList?.audioVolumeValue?.bgmVolume
              : 0.5,
        }));
      }
    };

    const [QuestScore, setAllQuestScore] = useState<QuestScoreType | null>(
      null,
    );

    const handleQuestScore = async () => {
      if (!gameInfo?.gameId || !gameInfo?.learnerDetails?.lenId) return;
      const data = {
        learnerId: gameInfo?.learnerDetails?.lenId,
        gameId: gameInfo?.gameId,
      };

      const AllQuest = await AllQestSingleScore(JSON.stringify(data));

      setAllQuestScore(AllQuest?.Data);
    };

    // useEffect(()=>{
    //   handleQuestScore()
    // },[currentScreenId])

    useEffect(() => {
      if (
        gameInfo?.gameId &&
        gameInfo?.learnerDetails?.lenId &&
        [13, 21, 19, 17, 16, 18, 22].includes(Number(currentScreenId))
      ) {
        handleQuestScore();
      }
    }, [currentScreenId, gameInfo?.gameId, gameInfo?.learnerDetails?.lenId]);

    const totalPoints = useMemo(() => {
      let total: number = 0;
      let TotalScore: number = 0;

      if ([2, 4, 6, 8, 9, 14, 15].includes(currentScreenId)) {
        const scoreArray =
          Object.entries(questState).length > 0
            ? questState[parseInt(profile?.currentQuest)] === 'Started'
              ? profile?.score
              : Object.entries(questState).length > 0
              ? questState[parseInt(profile?.currentQuest)] ===
                  'replayallowed' ||
                questState[parseInt(profile?.currentQuest)] === 'completed'
                ? profile?.replayScore
                : profile?.score
              : profile?.score
            : profile?.score;
        if (scoreArray?.length > 0) {
          // STEP 2: Apply filter with logs
          const filteredData = scoreArray?.filter((cur: any, index: number) => {
            const isValidStructure =
              cur.seqId &&
              cur.scenarioId !== undefined &&
              cur.choosedoption !== undefined;

            const isQuestMatch =
              Number(cur.quest) === Number(profile.currentQuest);

            const finalResult = isValidStructure && isQuestMatch;

            return finalResult;
          });

          // STEP 3: Reduce with logs
          total = filteredData?.reduce(
            (sum: number, cur: any, index: number) => {
              const score = Number(cur.score || 0);

              return sum + score;
            },
            0,
          );
        }
        if ([4, 6, 8, 9, 14].includes(currentScreenId)) {
          TotalScore = profile?.score?.reduce((acc: number, cur: any) => {
            if (cur.quest === parseInt(profile.currentQuest)) {
              return acc + (cur.score || 0);
            } else {
              return acc;
            }
          }, 0);
        }
        return isNaN(total) || total === 0 ? TotalScore : total;
      } else {
        const scoreArray =
          Object.entries(questState).length > 0
            ? questState[parseInt(profile?.currentQuest)] === 'Started'
              ? profile?.score
              : Object.entries(questState).length > 0
              ? questState[parseInt(profile?.currentQuest)] ===
                  'replayallowed' ||
                questState[parseInt(profile?.currentQuest)] === 'completed'
                ? profile?.replayScore?.length > 0
                  ? profile?.replayScore
                  : profile?.score
                : profile?.score
              : profile?.score
            : profile?.score;
        const scores = scoreArray;
        const sums: any = {};
        scores?.forEach((score: any) => {
          const quest = score.quest;
          if (!sums[quest]) {
            sums[quest] = 0;
          }
          sums[quest] += score.score;
        });

        let getFinalscores = {};

        Object.entries(sums).forEach(([quest, score]) => {
          const IntQuest = parseInt(quest);
          const newQuest = { ...getFinalscores, [IntQuest]: score };
          getFinalscores = { ...newQuest };
        });

        const Replayscores =
          profile?.replayScore?.length > 0 ? profile?.replayScore : null;

        const Replaysums: { [key: number]: number } = {};
        Replayscores?.forEach((score: any) => {
          const quest = score.quest;
          if (!Replaysums[quest]) {
            Replaysums[quest] = 0;
          }
          Replaysums[quest] += score.score || 0;
        });

        let getReplayFinalscores: { [key: number]: number } = {};
        Object.entries(Replaysums).forEach(([quest, score]) => {
          const IntQuest = parseInt(quest);
          getReplayFinalscores = { ...getReplayFinalscores, [IntQuest]: score };
        });

        total = Object.entries(getFinalscores).reduce(
          (tot: number, acc: any) => {
            let newTotal = tot;
            let questNo = acc[0];
            let questHasReplay = Object.keys(getReplayFinalscores).some(
              (quest) => quest === questNo,
            );
            if (questHasReplay) {
              getReplayFinalscores[questNo] > acc[1]
                ? (tot += getReplayFinalscores[questNo])
                : (tot += acc[1]);
            } else {
              tot += acc[1];
            }
            return tot;
          },
          0,
        );
      }

      const apiTotal = QuestScore?.totalQuestScore ?? 0;

      const calculatedTotal = isNaN(total) || total === 0 ? TotalScore : total;

      if ([13, 21, 19, 17, 16, 18, 22].includes(currentScreenId)) {
        return apiTotal;
      }
      return Math.max(calculatedTotal, apiTotal);
    }, [
      profile?.score?.length,
      profile?.replayScore?.length,
      currentScreenId,
      profile?.score,
      profile?.replayScore,
      QuestScore,
    ]);

    const leaderboardToRender = useMemo(() => {
      const currentBoard =
        selectedTab === 'department'
          ? departmentLeaderboardData
          : companyLeaderboardData;
      const data = Array.isArray(currentBoard) ? currentBoard.slice() : [];

      // Map scores to positions (daily)
      const dailySorted = data
        .slice()
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
      const dailyPos = new Map<number | string, number>();
      dailySorted.forEach((item, idx) => {
        const key = item.learnerId ?? item.name ?? idx;
        dailyPos.set(key, idx + 1);
      });

      // Map allTimeScore to positions (all time)
      const alltimeSorted = data
        .slice()
        .sort((a, b) => (b.allTimeScore ?? 0) - (a.allTimeScore ?? 0));
      const alltimePos = new Map<number | string, number>();
      alltimeSorted.forEach((item, idx) => {
        const key = item.learnerId ?? item.name ?? idx;
        alltimePos.set(key, idx + 1);
      });

      // Attach positions
      const augmented = data.map((item, idx) => {
        const key = item.learnerId ?? item.name ?? idx;
        return {
          ...item,
          dailyPosition: dailyPos.get(key) ?? idx + 1,
          alltimePosition: alltimePos.get(key) ?? idx + 1,
        };
      });

      // Order by daily score for display (highest first)
      const ordered = augmented
        .slice()
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

      // If you want the currently-playing user to appear visually at the top,
      // find them and move their row to index 0 (positions remain unchanged).
      const playerName =
        learnerPlayList?.LearnerProfile?.nickName ??
        learnerPlayList?.LearnerProfile?.name;
      if (playerName) {
        const playerIndex = ordered.findIndex((it) => it.name === playerName);
        if (playerIndex > -1) {
          const [playerRow] = ordered.splice(playerIndex, 1);
          ordered.unshift(playerRow);
        }
      }

      return ordered;
    }, [
      selectedTab,
      departmentLeaderboardData,
      companyLeaderboardData,
      learnerPlayList,
    ]);

    const handleNavigate10 = () => {
      navigate('/game/gamePlay');
    };

    const handleNavigate13 = () => {
      if (currentScreenId === 13) {
        return false;
      } else {
        useData?.setMotionEffect(true);
        setTimeout(() => {
          setCurrentScreenId(13);
        }, 300);
      }
    };

    return (
      <>
        <div className="relative flex h-full w-full  px-4 sm:px-6 lg:px-8">
          {dontShowTopMenu && !isSettingOpen && !isLeaderboardEnabled ? (
            <>
              {/* Top Menu - Home, Map, Progress, Settings, Leaderboard */}
              <div
                className="absolute left-0 right-0 top-0 flex h-[5.5rem] items-center justify-between bg-cover bg-center bg-no-repeat px-4 sm:px-6 lg:px-8"
                style={{
                  backgroundImage: `url(${TopNavBar})`,
                  zIndex: '99999',
                }}
              >
                <div className="flex items-center gap-6">
                  {![1, 12, 20].includes(currentScreenId) && (
                    <>
                      <TopNavButton
                        icon="exit"
                        //  onClick={() => handleNavigate10()}
                        onClick={() => {
                          playMenuSound();
                          handleNavigate10();
                        }}
                      />

                      {/* <TopNavButton
    icon="pause"
    onClick={() => setIsSettingOpen(true)} // or your pause handler
  /> */}
                      {/* <TopNavButton 
icon={audioObj.paused ? "play" : "pause"}
 onClick={() =>
   setAudioObj((prev:any) => ({
   ...prev,
  paused: !prev.paused,
  }))
 }
 /> */}

                      <TopNavButton
                        icon="home"
                        //  onClick={() => handleNavigate13()}
                        onClick={() => {
                          playMenuSound();
                          handleNavigate13();
                        }}
                      />
                      <TopNavButton
                        icon={isAudioPaused ? 'play' : 'pause'}
                        // onClick={toggleAudioPlayPause}
                        onClick={() => {
                          playMenuSound();
                          toggleAudioPlayPause();
                        }}
                      />
                    </>
                  )}
                </div>

                {/* Status Bars - Progress and Score */}
                <div className="flex items-center gap-4">
                  <StatusBar
                    label={Math.floor(progressPercent * 100).toString()}
                    icon="progress"
                    newlabel={totalPoints.toString()}
                  />
                  {/* <StatusBar label={totalPoints.toString()} icon="coin" />
                   */}
                  <StatusBar
                    label={totalPoints.toString()}
                    icon="coin"
                    newlabel={totalPoints.toString()}
                  />

                  {/* Leaderboard */}
                  {gameInfo?.gameData?.gameIsShowLeaderboard === 'true' && (
                    <TopNavButton
                      icon="ranking"
                      // onClick={() => setIsLeaderboardEnabled(true)}
                      onClick={() => {
                        playMenuSound();
                        setIsLeaderboardClicked(true);
                        setIsLeaderboardEnabled(true);
                      }}
                      // pointerEvents={isButtonDisabled ? 'none' : 'auto'}
                    />
                  )}
                  <div className="translate-y-1">
                    <TopNavButton
                      icon="settings"
                      //  onClick={() => setIsSettingOpen(true)}
                      onClick={() => {
                        playMenuSound();
                        setIsSettingOpen(true);
                      }}
                    />
                  </div>
                  {/* Settings */}
                </div>
              </div>
            </>
          ) : null}
          <Dialog open={isSettingOpen} onOpenChange={setIsSettingOpen}>
            {/* <DialogTrigger asChild>
                <div className="translate-y-1">
                  <TopNavButton icon="settings" />
                </div>
              </DialogTrigger> */}
            <DialogContent
              className="rounded-[2rem] border-none px-7 py-6 shadow-lg sm:max-w-2xl"
              style={{
                background: `linear-gradient(151.477deg, rgb(0,0,0) 17.606%, rgba(${primaryColorRgb}, 0.6) 218.68%)`,
                border: `1px solid linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${primaryColorRgb}, 0.4) 188.68%)`,
                boxShadow: '0px 0px 23.4px 0px rgba(0, 0, 0, 0.50)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <h2
                className="py-1 text-center text-3xl font-medium"
                style={{
                  color: primaryColor,
                  textShadow: `0 0 9px rgba(${primaryColorRgb}, 0.49)`,
                }}
              >
                Settings
              </h2>

              {/* Decorative Line */}
              <div className="relative flex w-full items-center justify-center">
                <div className="w-full">
                  <div className="relative h-[1px] w-full">
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(90deg, transparent 0%, ${primaryColor} 48%, transparent 100%)`,
                        opacity: 0.995,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mx-auto mb-2 mt-3 flex w-full max-w-md flex-col gap-8 pb-3 pt-5">
                <div className="flex flex-col gap-10">
                  {/* Music Volume */}
                  <div className="flex flex-col items-center gap-1">
                    <label className="font-rubik mb-2 text-2xl font-normal tracking-[-0.005rem] text-white">
                      Music Volume
                    </label>
                    <Slider
                      defaultValue={[
                        learnerPlayList?.audioVolumeValue?.bgmVolume
                          ? learnerPlayList?.audioVolumeValue?.bgmVolume * 100
                          : audioObj?.BGMvolume * 100,
                      ]}
                      max={100}
                      step={1}
                      className="w-full"
                      onChangeEnd={(value: any) =>
                        currentScreenId === 2 || currentScreenId === 14
                          ? null
                          : handleMusicVolume(value, EnumType.BGM)
                      }
                      // trackClassName="bg-white/70"
                      // thumbClassName="bg-white border border-gray-300 shadow-sm"
                    />
                  </div>
                  {/* Voice Over Volume */}
                  <div className="flex flex-col items-center gap-1">
                    <label className="font-rubik mb-2 text-2xl font-normal tracking-[-0.005rem] text-white">
                      Voice over volume
                    </label>
                    <Slider
                      defaultValue={[
                        learnerPlayList?.audioVolumeValue?.voiceVolume
                          ? learnerPlayList?.audioVolumeValue?.voiceVolume * 100
                          : audioObj?.Voicevolume * 100,
                      ]}
                      max={100}
                      step={1}
                      className="w-full"
                      onChangeEnd={(value) =>
                        handleMusicVolume(value, EnumType.VOICE)
                      }
                      // trackClassName="bg-white/70"
                      // thumbClassName="bg-white border border-gray-300 shadow-sm"
                    />
                  </div>
                  {/* Color Picker */}
                  {/* <div className="flex flex-col gap-1 items-center">
                      <label className="text-2xl font-rubik mb-2 text-white font-normal tracking-[-0.005rem]">
                        Set Theme
                      </label>
                      <ColorPicker />
                    </div> */}
                </div>

                {/* Okay button */}
                <DialogTrigger asChild>
                  <Buttons
                    variant="ghost"
                    size="sm"
                    className="mx-auto h-11 w-[5.75rem] rounded-3xl px-4 py-2 text-lg text-white transition-all duration-200 hover:scale-110 hover:text-white"
                    style={{
                      background: `linear-gradient(275.041deg, rgba(${primaryColorRgb}, 0.7) 7.25%, rgba(0, 0, 0, 0.8) 84.803%)`,
                      border: `1px solid ${primaryColor}`,
                      boxShadow: `0px 0px 15.9542px 0px rgba(3, 51, 38, 0.8)`,
                    }}
                  >
                    Okay
                  </Buttons>
                </DialogTrigger>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Leaderboard Modal */}
        {isLeaderboardEnabled === true && (
          <motion.div
            initial={{ rotateY: 180, y: -100 }}
            animate={{ rotateY: 0, y: 0 }}
            transition={{ type: 'spring', duration: 1 }}
            className="absolute bottom-[50px] left-0 z-[99999] flex h-full w-full items-center justify-center"
          >
            {/* Leadership Content */}
            <div className="flex h-full flex-grow flex-col items-center justify-center pt-[7.25rem]">
              <div
                className="md2x:pt-8 md2x:pb-8 relative flex h-full max-h-fit w-full max-w-7xl flex-col items-center justify-start gap-5 overflow-hidden rounded-[2.375rem] px-10 pb-6 pt-6 backdrop-blur-3xl"
                style={{
                  background: `linear-gradient(21deg, rgba(0, 0, 0, 0.80) -10.76%, rgba(${primaryColorRgb}, 0.2) 107.18%)`,
                  boxShadow: `0px 0px 11px 0px inset ${primaryColor}`,
                }}
              >
                {/* Leaderboard Title */}
                <div className="relative">
                  <h1
                    className="text-center text-4xl font-normal tracking-normal"
                    style={{
                      color: primaryColor,
                      textShadow: `rgba(${primaryColorRgb}, 0.5) 0px 0px 9px`,
                    }}
                  >
                    Leaderboard
                  </h1>
                </div>

                {/* Decorative Line */}
                <div className="relative flex w-full items-center justify-center">
                  <div className="w-full">
                    <div className="relative h-[1px] w-full">
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(90deg, transparent 0%, ${primaryColor} 48%, transparent 100%)`,
                          opacity: 0.995,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tabs using ShadCN */}
                <div className="max-md2x:overflow-y-auto w-full">
                  <Tabs
                    value={selectedTab}
                    onValueChange={(value) =>
                      setSelectedTab(value as 'department' | 'overall')
                    }
                    className="w-full"
                  >
                    <TabsList
                      className="max-md2x:position-sticky max-md2x:top-0 max-md2x:z-10 grid w-full grid-cols-2 gap-3 bg-transparent p-0"
                      style={{
                        background: 'transparent',
                      }}
                    >
                      <TabsTrigger
                        value="overall"
                        className="font-rubik rounded-sm border px-3 py-3.5 text-xl font-medium tracking-[-0.048px] transition-all duration-200 hover:scale-[1.01] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        style={{
                          borderWidth: '0.548px',
                          borderColor:
                            selectedTab === 'overall'
                              ? `rgba(${primaryColorRgb}, 0.5)`
                              : '#000000',
                          color:
                            selectedTab === 'overall'
                              ? primaryColor
                              : 'rgba(255,255,255,0.5)',
                          boxShadow: '0px 0px 12.485px 0px rgba(0,0,0,0.5)',
                          opacity: selectedTab === 'overall' ? 1 : 0.8,
                          background: `linear-gradient(21deg, rgba(${primaryColorRgb}, 0.02) -10.76%, rgba(${primaryColorRgb}, 0.1) 127.18%)`,
                        }}
                      >
                        Overall
                      </TabsTrigger>
                      <TabsTrigger
                        value="department"
                        className="font-rubik rounded-sm border px-3 py-3.5 text-xl font-medium tracking-[-0.048px] transition-all duration-200 hover:scale-[1.01] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        style={{
                          borderWidth: '0.548px',
                          borderColor:
                            selectedTab === 'department'
                              ? `rgba(${primaryColorRgb}, 0.5)`
                              : '#000000',
                          color:
                            selectedTab === 'department'
                              ? primaryColor
                              : 'rgba(255,255,255,0.5)',
                          boxShadow: '0px 0px 12.485px 0px rgba(0,0,0,0.5)',
                          opacity:
                            selectedTab === 'department' && !isDepartmentEmpty
                              ? 1
                              : 0.5,
                          background: `linear-gradient(21deg, rgba(${primaryColorRgb}, 0.02) -10.76%, rgba(${primaryColorRgb}, 0.1) 127.18%)`,
                          cursor: isDepartmentEmpty ? 'not-allowed' : 'pointer',
                        }}
                        onClick={handleDepartmentClick}
                        disabled={isDepartmentEmpty}
                      >
                        Department
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="overall" className="mt-4">
                      <div
                        className="relative w-full shrink-0 rounded-[0.564rem]"
                        ref={containerRef}
                      >
                        <div
                          className="box-border flex w-full flex-col content-stretch items-start justify-start overflow-clip rounded-md p-2"
                          style={{
                            backgroundImage:
                              'linear-gradient(90deg, rgba(0, 0, 0, 0.63) 0%, rgba(0, 0, 0, 0.63) 100%)',
                            backgroundSize: 'auto, cover',
                            backgroundPosition: '0% 0%, 50% 50%',
                          }}
                        >
                          <Table className="min-w-[800px] w-full">
                            <TableHeader>
                              <TableRow className="border-b-0 hover:bg-transparent">
                                <TableHead className="font-poppins h-[1.879rem] w-[10.38rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Player Name
                                </TableHead>
                                <TableHead
                                  className="font-poppins h-[1.879rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] px-4 text-center text-sm font-semibold text-[#7f7d83]"
                                  colSpan={2}
                                >
                                  Daily
                                </TableHead>
                                <TableHead
                                  className="font-poppins h-[1.879rem] bg-[rgba(0,0,0,0.5)] px-4 text-center text-sm font-semibold text-[#7f7d83]"
                                  colSpan={2}
                                >
                                  All Time
                                </TableHead>
                              </TableRow>
                              <TableRow className="border-b-0 hover:bg-transparent">
                                <TableHead className="h-[1.879rem] w-[10.38rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)]"></TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Position
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Score
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Position
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Score
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                          </Table>
                          <div className="w-full
    overflow-y-auto
    overflow-x-auto
                          
                          "
                            style={{
                              maxHeight: "15rem",
                            }}>
                            <Table className="min-w-[800px] w-full">
                              <TableBody>
                                {leaderboardToRender.map((player, index) => {
                                  const isUser =
                                    player.name ===
                                    (learnerPlayList?.LearnerProfile
                                      ?.nickName ??
                                      learnerPlayList?.LearnerProfile?.name);
                                  return (
                                    <TableRow
                                      key={index}
                                      className="h-[3.188rem] border-b"
                                      style={{
                                        borderBottomColor: isUser
                                          ? primaryColor
                                          : '#3d3d3d',
                                        borderBottomWidth: '0.751px',
                                        backgroundColor: isUser
                                          ? `rgba(${primaryColorRgb},0.15)`
                                          : '',
                                      }}
                                    >
                                      <TableCell
                                        className="font-rubik w-[10.38rem] border-r border-[#3d3d3d] pl-[1.691rem] text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.name || 'Guest'}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.dailyPosition ?? index + 1}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.score ?? 0}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.alltimePosition ?? index + 1}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                        }}
                                      >
                                        {player.allTimeScore ?? 0}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="department" className="mt-4 w-full flex flex-col min-h-0">
                      <div
                        className=" w-full overflow-x-auto"
                        ref={containerRef}
                      >
                        <div
                          className="box-border flex w-full flex-col content-stretch items-start justify-start overflow-clip rounded-md p-2"
                          style={{
                            backgroundImage:
                              'linear-gradient(90deg, rgba(0, 0, 0, 0.63) 0%, rgba(0, 0, 0, 0.63) 100%)',
                            backgroundSize: 'auto, cover',
                            backgroundPosition: '0% 0%, 50% 50%',
                          }}
                        >
                          <Table className="w-full table-fixed">
                            <TableHeader>
                              <TableRow className="border-b-0 hover:bg-transparent">
                                <TableHead className="font-poppins h-[1.879rem] w-[10.38rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Player Name
                                </TableHead>
                                <TableHead
                                  className="font-poppins h-[1.879rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] px-4 text-center text-sm font-semibold text-[#7f7d83]"
                                  colSpan={2}
                                >
                                  Daily
                                </TableHead>
                                <TableHead
                                  className="font-poppins h-[1.879rem] bg-[rgba(0,0,0,0.5)] px-4 text-center text-sm font-semibold text-[#7f7d83]"
                                  colSpan={2}
                                >
                                  All Time
                                </TableHead>
                              </TableRow>
                              <TableRow className="border-b-0 hover:bg-transparent">
                                <TableHead className="h-[1.879rem] w-[10.38rem] border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)]"></TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Position
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Score
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 border-r border-[#3d3d3d] bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Position
                                </TableHead>
                                <TableHead className="font-poppins h-[1.879rem] w-24 bg-[rgba(0,0,0,0.5)] text-center text-sm font-semibold text-[#7f7d83]">
                                  Score
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                          </Table>
                          <div className="w-full
    overflow-y-auto
    overflow-x-auto"
                            style={{
                              maxHeight: "15rem",
                            }}>
                            <Table className="min-w-[800px] w-full">
                              <TableBody>
                                {leaderboardToRender.map((player, index) => {
                                  const isUser =
                                    player.name ===
                                    (learnerPlayList?.LearnerProfile
                                      ?.nickName ??
                                      learnerPlayList?.LearnerProfile?.name);
                                  return (
                                    <TableRow
                                      key={index}
                                      className="h-[3.188rem] border-b"
                                      style={{
                                        borderBottomColor: isUser
                                          ? primaryColor
                                          : '#3d3d3d',
                                        borderBottomWidth: '0.751px',
                                        backgroundColor: isUser
                                          ? `rgba(${primaryColorRgb},0.15)`
                                          : '',
                                      }}
                                    >
                                      <TableCell
                                        className="font-rubik w-[10.38rem] border-r border-[#3d3d3d] pl-[1.691rem] text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.name || 'Guest'}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.dailyPosition ?? index + 1}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.score ?? 0}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 border-r border-[#3d3d3d] text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                          borderRightColor: '#3d3d3d',
                                        }}
                                      >
                                        {player.alltimePosition ?? index + 1}
                                      </TableCell>
                                      <TableCell
                                        className="font-rubik w-24 text-center text-base font-semibold tracking-[-0.0376px]"
                                        style={{
                                          color: isUser
                                            ? primaryColor
                                            : '#ffffff',
                                        }}
                                      >
                                        {player.allTimeScore ?? 0}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Bottom Navigation */}
                <div className="flex w-full items-center justify-between">
                  <Buttons
                    variant="ghost"
                    size="sm"
                    className="mx-auto h-11 w-[5.75rem] rounded-3xl px-4 py-2 text-lg text-white transition-all duration-200 hover:scale-110 hover:text-white"
                    // style={{
                    //   background: `linear-gradient(275.041deg, rgba(${primaryColorRgb}, 0.7) 7.25%, rgba(3, 51, 38, 0.8) 84.803%)`,
                    //   border: `1px solid ${primaryColor}`,
                    //   boxShadow: `0px 0px 15.9542px 0px rgba(3, 51, 38, 0.8)`,
                    // }}
                     style={{
                        background: `linear-gradient(100deg ,
                        rgba(${hexToRgb(
                          primaryColor,
                        )}) -27.25%, rgba(0, 0, 0, 0.8) 127.803%)`,
                        border: `1px solid rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.5)`,
                        boxShadow: `0px 0px 21.786px 0px rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.4)`,
                      }}
                    onClick={() => setIsLeaderboardEnabled(false)}
                  >
                    Close
                  </Buttons>
                </div>

                {/* Inset Shadow Border */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-[2.375rem]"
                  style={{
                    boxShadow: `0px 0px 11px 0px inset rgba(${primaryColorRgb}, 0.3), 0px 0px 20px 0px rgba(${primaryColorRgb}, 0.2)`,
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </>
    );
  },
);

export default TopMenuBarFutureTheme;
